import {
  LoadingSpinner,
  Modal,
  getCurrencyFormat,
  formatUnixTime,
} from '@rabbit/elements/shared-components';
import { useGetWarrantyHistory, useGetAuditLogs } from '@rabbit/bizproc/react';
import { DTWarranty, DTAuditLog } from '@rabbit/data/types';
import { ArrowLongRightIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@rabbit/app-context';
import { useContext, useEffect, useState } from 'react';
import moment from 'moment';

export interface ModalHistoryLogProps {
  handleClose: () => void;
  warrantyLink: string;
  entityType?: string;
  entityLink?: string;
  tenantLink?: string;
}

const type = (o: any) => Object.prototype.toString.call(o).slice(8, -1);
const parseIfJson = (value: any) => {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (e) {
      return value; // Return as is if parsing fails
    }
  }
  return value;
};
const differ = (o1: any, o2: any, parentKey: string = ''): any => {
  const diff: any = {};
  const keys = new Set([...Object.keys(o1), ...Object.keys(o2)]);
  for (const key of keys) {
    const fullKey = parentKey ? `${parentKey}.${key}` : key;
    let val1 = o1[key];
    let val2 = o2[key];
    if (key === 'decider') {
      val1 = parseIfJson(val1);
      val2 = parseIfJson(val2);
    }
    if (Array.isArray(val1) && Array.isArray(val2)) {
      const maxLength = Math.max(val1.length, val2.length);
      for (let i = 0; i < maxLength; i++) {
        const item1 = val1[i];
        const item2 = val2[i];
        const arrayKey = `${fullKey}.${i}`;
        if (type(item1) === 'Object' && type(item2) === 'Object') {
          const nestedDiff = differ(item1, item2, arrayKey);
          if (Object.keys(nestedDiff).length > 0) {
            Object.assign(diff, nestedDiff);
          }
        } else if (item1 !== item2) {
          diff[arrayKey] = [item1, item2];
        }
      }
    } else if (type(val1) === 'Object' && type(val2) === 'Object') {
      const nestedDiff = differ(val1, val2, fullKey);
      if (Object.keys(nestedDiff).length > 0) {
        Object.assign(diff, nestedDiff);
      }
    } else if (val1 !== val2) {
      diff[fullKey] = [val1, val2];
    }
  }
  return diff;
};

export function ModalHistoryLog({
  handleClose,
  warrantyLink,
  entityType,
  entityLink,
  tenantLink,
}: ModalHistoryLogProps) {
  const { tenantInfo, config } = useContext(AppContext);
  const { t } = useTranslation();
  const { warrantyHistory } = useGetWarrantyHistory(
    warrantyLink || '',
    t('tenantLink')
  ) || {
    warrantyHistory: null,
  };
  // Use the hook to get audit logs
  const { auditLogs, isLoading: auditLogsLoading } = useGetAuditLogs({
    entityType,
    entityLink,
    tenantLink,
  });

  const [warrantyHistoryLoading, setWarrantyHistoryLoading] =
    useState<boolean>(true);

  // Effect to track when warranty history is loaded
  useEffect(() => {
    if (warrantyHistory !== null) {
      setWarrantyHistoryLoading(false);
    }
  }, [warrantyHistory]);

  const mileageUnit =
    (warrantyHistory?.[0].SRVSnapshot?.srvInfo?.productInfo as any)
      ?.mileageUnit || 'km';
  const buildLog = () => {
    if (!warrantyHistory) return [];
    const all: any = [];
    const logLength = warrantyHistory.length;
    for (let i = 0; i < logLength - 1; i++) {
      const currentWarranty: DTWarranty = warrantyHistory[i];
      const previousWarranty: DTWarranty = warrantyHistory[i + 1];
      all.push(differ(currentWarranty, previousWarranty));
    }
    return all;
  };

  const groupByDate = (log: any) => {
    const days = log.reduce((acc: any, curr: any) => {
      const date = new Date(
        curr.tupdate ? curr.tupdate[1] : acc.tupdate ? acc.tupdate[1] : 0
      ).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].unshift(curr);
      return acc;
    }, {});

    // Sort the logs by time
    for (let key in days) {
      days[key] = days[key].sort(
        (a: any, b: any) =>
          new Date(b.tupdate).getTime() - new Date(a.tupdate).getTime()
      );
    }
    return days;
  };

  const groupAuditLogsByDate = (logs: DTAuditLog[]) => {
    return logs.reduce((acc: Record<string, DTAuditLog[]>, log) => {
      const date = new Date(log.timestamp).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(log);
      return acc;
    }, {});
  };

  const combineAndSortLogs = () => {
    const warrantyLogs = buildLog();

    // Add logType property to warranty logs
    const typedWarrantyLogs = warrantyLogs.map((log: any) => ({
      ...log,
      logType: 'warranty',
    }));

    const groupedWarrantyLogs = groupByDate(typedWarrantyLogs);

    // If audit logs are not enabled or not available, return only warranty logs
    if (!config?.HOLDINGS?.SHOW_AUDIT_LOG || auditLogs.length === 0) {
      return groupedWarrantyLogs;
    }

    const groupedAuditLogs = groupAuditLogsByDate(auditLogs);

    // Combine both types of logs
    const combinedLogs: Record<string, any[]> = { ...groupedWarrantyLogs };

    // Add audit logs to the combined logs
    Object.keys(groupedAuditLogs).forEach((date) => {
      if (!combinedLogs[date]) {
        combinedLogs[date] = [];
      }

      // Add a type property to distinguish between warranty logs and audit logs
      const typedAuditLogs = groupedAuditLogs[date].map((log: DTAuditLog) => ({
        ...log,
        logType: 'audit',
      }));

      combinedLogs[date] = [...combinedLogs[date], ...typedAuditLogs];

      // Sort logs by timestamp (for audit logs) or tupdate (for warranty logs)
      combinedLogs[date].sort((a, b) => {
        const timeA =
          a.logType === 'audit'
            ? a.timestamp
            : new Date(a.tupdate[1]).getTime();
        const timeB =
          b.logType === 'audit'
            ? b.timestamp
            : new Date(b.tupdate[1]).getTime();
        return timeB - timeA;
      });
    });

    return combinedLogs;
  };

  const logs = combineAndSortLogs();

  const renderRow = (label: string, obj: any[]) => {
    function checkValue(value: any) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }

      if (typeof value === 'object' && value !== null) {
        if (
          label.toLowerCase().includes('price') &&
          value.amount !== undefined &&
          value.currency !== undefined
        ) {
          return getCurrencyFormat(value.amount, value.currency);
        }
        return JSON.stringify(value);
      }

      if (
        label.toLowerCase().includes('price') ||
        label.toLowerCase().includes('vat') ||
        label.toLowerCase().includes('rate')
      ) {
        if (label.toLowerCase().includes('rate')) {
          value =
            getCurrencyFormat(value, tenantInfo?.currency ?? 'GBP') + '/h';
        } else value = getCurrencyFormat(value, tenantInfo?.currency ?? 'GBP');
      }
      if (label.toLowerCase().includes('date')) {
        if (value === '') return '-';
        if (type(value) === 'String') {
          // Handle special case for 'lifetime' value
          if (value.toLowerCase() === 'lifetime') {
            return value;
          }
          return value;
        }
        if (value) {
          value = new Date(value).toLocaleDateString();
        } else {
          value = '-';
        }
      }
      if (type(value) === 'Boolean') {
        value = value ? 'Yes' : 'No';
      }
      if (label.toLowerCase().includes('mileage')) {
        value = value + mileageUnit;
      }
      if (
        label.toLowerCase().includes('status') &&
        typeof value === 'string' &&
        value.length > 0
      ) {
        value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      }
      return value;
    }
    const current = checkValue(obj[1]);
    const previous = checkValue(obj[0]);
    if (current === '-' && previous === '-') return null;
    return (
      <div
        className="text-md flex gap-2 overflow-hidden text-gray-500"
        key={label}
      >
        <p className="whitespace-nowrap">{label}:</p>
        <p className="overflow-hidden text-ellipsis whitespace-nowrap line-through">
          {previous}
        </p>
        <ArrowLongRightIcon width={20} className="flex-shrink-0" />
        <p className="overflow-hidden text-ellipsis whitespace-nowrap">
          {current}
        </p>
      </div>
    );
  };

  const SRVKeys: Record<string, string> = {
    'SRVSnapshot.srvInfo.productInfo.regDate': 'Reg date',
    'SRVSnapshot.srvInfo.productInfo.fuel': 'Fuel',
    'SRVSnapshot.srvInfo.productInfo.mileage': 'Mileage',
    'decider.stipulated.riskCategory.label': 'Risk category',
    'decider.stipulated.isHybrid': 'Hybrid',
    'decider.stipulated.isCommercial': 'Commercial vehicle',
    'decider.stipulated.is4x4': '4x4',
    'SRVSnapshot.srvInfo.productInfo.techCheckDate': 'Tech check date',
  };
  const warrantyKeys: Record<string, string> = {
    templateName: 'Warranty plan',
    status: 'Warranty status',
    'price.amount': 'Price',
    startDate: 'Start date',
    endDate: 'End date',
    'decider.stipulated.claimLimit.label': 'Claim limit',
    'decider.stipulated.duration.label': 'Duration',
    'decider.decided.warrantyPrice': 'Warranty price',
    'decider.decided.VAT': 'VAT',
    'appliedOptions.2.value': 'Labour rate',
    registeredTime: 'Registration date',
  };

  const renderDetails = (obj: any, keys: any) => {
    let modifiedObj = { ...obj };
    const durationKey = Object.keys(obj).find(
      (key) => keys[key] === 'Duration'
    );
    let dontShowEndDate = false;

    const endDateKey = Object.keys(obj).find((key) => keys[key] === 'End date');
    const startDateKey = Object.keys(obj).find(
      (key) => keys[key] === 'Start date'
    );
    if (endDateKey && startDateKey) {
      const originalEndDate = obj[endDateKey];
      const originalStartDate = obj[startDateKey];
      const diffYear = moment(originalEndDate[1]).diff(
        moment(originalStartDate[1]),
        'years',
        true
      );
      if (diffYear > 1) {
        dontShowEndDate = true;
      }
    }

    if (durationKey && obj[durationKey]) {
      const endDateKey = Object.keys(obj).find(
        (key) => keys[key] === 'End date'
      );

      if (endDateKey) {
        const originalEndDate = obj[endDateKey];
        const durationArray = obj[durationKey];

        if (Array.isArray(durationArray) && Array.isArray(originalEndDate)) {
          let newEndDate = [...originalEndDate];

          if (
            durationArray[0] &&
            typeof durationArray[0] === 'string' &&
            durationArray[0].toLowerCase().includes('lifetime')
          ) {
            newEndDate[0] = 'lifetime';
          }

          if (
            durationArray[1] &&
            typeof durationArray[1] === 'string' &&
            durationArray[1].toLowerCase().includes('lifetime')
          ) {
            newEndDate[1] = 'lifetime';
          }

          modifiedObj[endDateKey] = newEndDate;
        } else if (
          !Array.isArray(durationArray) &&
          typeof durationArray === 'string' &&
          durationArray.toLowerCase().includes('lifetime')
        ) {
          modifiedObj[endDateKey] = 'lifetime';
        }
      }
    }

    return (
      <div className={sectionStyles}>
        {Object.keys(modifiedObj).map((key: string) => {
          if (key in keys) {
            if (
              !keys[key].toLowerCase().includes('registration date') &&
              !(dontShowEndDate && keys[key].toLowerCase().includes('end date'))
            ) {
              const values =
                Array.isArray(modifiedObj[key]) && modifiedObj[key].length === 2
                  ? modifiedObj[key]
                  : [null, modifiedObj[key]];
              return renderRow(keys[key], values);
            }
          }
          return null;
        })}
      </div>
    );
  };

  const renderActionType = (action: string) => {
    switch (action) {
      case 'updateconsumer':
        return t('auditLogs.customerUpdated');
      case 'updateholding':
        return t('auditLogs.holdingUpdated');
      default:
        return t('auditLogs.customerModified');
    }
  };

  const textStyles =
    'font-nunito overflow-hidden text-ellipsis whitespace-nowrap';
  const sectionStyles =
    'flex flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2';

  const consumerFields = [
    { key: 'fullName', fallback: '-' },
    { key: 'email', fallback: '-' },
    { key: 'telephone', fallback: '-' },
  ];

  const renderConsumerData = (title: string, consumer: any) => {
    return (
      <div className="flex flex-col gap-1">
        <p className={`${textStyles} font-semibold`}>{t(title)}</p>
        {consumerFields.map((field) => (
          <p key={field.key} className={textStyles}>
            {consumer[field.key] === undefined ||
            consumer[field.key] === null ||
            consumer[field.key] === ''
              ? field.fallback
              : consumer[field.key]}
          </p>
        ))}
      </div>
    );
  };

  const renderConsumerChange = (log: DTAuditLog) => {
    const oldConsumer = log.data.oldConsumerData || {};
    const newConsumer = log.data.newConsumerData || {};
    const transferReason = log.data.transferReason;

    return (
      <div className={sectionStyles}>
        <p className={`${textStyles} font-bold`}>
          {t('auditLogs.consumerDetails')}
        </p>
        <div className="grid grid-cols-2 gap-2">
          {renderConsumerData('auditLogs.oldCustomer', oldConsumer)}
          {renderConsumerData('auditLogs.newCustomer', newConsumer)}
        </div>
        {transferReason && (
          <div className="mt-2 border-t border-gray-200 pt-2">
            <p className={`${textStyles} font-semibold`}>
              {t('general.transferReason')}:
            </p>
            <p className={`${textStyles} text-gray-700`}>{transferReason}</p>
          </div>
        )}
      </div>
    );
  };

  const stringify = (obj: any) => {
    const replacer = (_key: string, value: any) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return Object.keys(value)
          .sort()
          .reduce((sorted: Record<string, any>, key: string) => {
            sorted[key] = value[key];
            return sorted;
          }, {});
      }
      return value;
    };
    return JSON.stringify(obj, replacer);
  };

  const renderHoldingChange = (log: DTAuditLog) => {
    const oldValues = log.data.oldValues || {};
    const newValues = log.data.newValues || {};

    const changedFields: Record<string, [any, any]> = {};

    Object.keys(newValues).forEach((key) => {
      if (key === 'vendable' || key === 'vendableTitle') {
        return;
      }

      if (
        key === 'additionalData' &&
        typeof oldValues[key] === 'object' &&
        oldValues[key] !== null &&
        typeof newValues[key] === 'object' &&
        newValues[key] !== null
      ) {
        const additionalDataKeys = new Set([
          ...Object.keys(oldValues[key] || {}),
          ...Object.keys(newValues[key] || {}),
        ]);

        additionalDataKeys.forEach((adKey) => {
          const oldValue = oldValues[key]?.[adKey];
          const newValue = newValues[key]?.[adKey];

          if (stringify(oldValue) !== stringify(newValue)) {
            if (
              adKey !== 'purchase_time' &&
              adKey !== 'holding_vendable_id' &&
              adKey !== 'purchase_price'
            ) {
              changedFields[`additionalData.${adKey}`] = [
                oldValue === undefined ? null : oldValue,
                newValue === undefined ? null : newValue,
              ];
            }
          }
        });
      } else if (stringify(oldValues[key]) !== stringify(newValues[key])) {
        changedFields[key] = [
          oldValues[key] === undefined ? null : oldValues[key],
          newValues[key] === undefined ? null : newValues[key],
        ];
      }
    });

    if (Object.keys(changedFields).length === 0) {
      return null;
    }

    const fieldLabels: Record<string, string> = {
      purchase_price: t('general.purchasePrice'),
      vendableTitle: t('general.product'),
      purchase_time: t('general.purchaseDate'),
    };

    Object.keys(changedFields).forEach((field) => {
      if (field.startsWith('additionalData.')) {
        const propertyName = field.replace('additionalData.', '');
        fieldLabels[field] =
          propertyName.charAt(0).toUpperCase() +
          propertyName.slice(1).replace(/([A-Z])/g, ' $1');
      }
    });

    return (
      <div className={sectionStyles}>
        <p className={`${textStyles} font-bold`}>
          {t('auditLogs.holdingDetails')}
        </p>
        <div className="flex flex-col gap-2">
          {Object.entries(changedFields).map(([field, values]) => {
            const label = fieldLabels[field] || field;
            return renderRow(label, values);
          })}
        </div>
      </div>
    );
  };
  const modalSettings = {
    title: t('general.historyLog'),
    handleClose: () => handleClose(),
  };
  return (
    <Modal kind="generic" settings={modalSettings} className="max-w-[45.25rem]">
      <div className="flex flex-col gap-3 px-4 py-4">
        {(warrantyHistoryLoading || auditLogsLoading) && (
          <div className={`${textStyles} text-lg text-gray-500`}>
            <LoadingSpinner size="sm" />
          </div>
        )}
        {!warrantyHistoryLoading &&
          !auditLogsLoading &&
          warrantyHistory !== null &&
          Object.keys(logs).length === 0 && (
            <div className={`${textStyles} p-4 text-center text-gray-500`}>
              No logs found
            </div>
          )}
        {!warrantyHistoryLoading &&
          !auditLogsLoading &&
          Object.keys(logs)
            .reverse()
            .map((date: string) => {
              return (
                <div key={date} className="flex flex-col gap-2">
                  <p
                    className={`${textStyles} text-md font-medium text-gray-500`}
                  >
                    {date}
                  </p>
                  {logs[date].map((log: any, index: number) => {
                    if (log.logType === 'audit') {
                      if (log.data.actionType === 'updateholding') {
                        const holdingChanges = renderHoldingChange(log);
                        if (!holdingChanges) {
                          return null;
                        }
                      }

                      return (
                        <div
                          key={`audit-${log.docid}-${index}`}
                          className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3 text-gray-500"
                        >
                          <p className={textStyles}>
                            {renderActionType(log.data.actionType)}
                          </p>

                          {log.data.actionType === 'updateconsumer' &&
                            renderConsumerChange(log)}

                          {log.data.actionType === 'updateholding' &&
                            renderHoldingChange(log)}

                          <div className="text-right text-sm">
                            {formatUnixTime(log.timestamp, 'HH:mm:ss')}
                            {log.data.actioner && log.data.actioner.email && (
                              <p
                                className={`${textStyles} text-xs text-gray-400`}
                              >
                                {t('auditLogs.by')} {log.data.actioner.email}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div
                          key={`warranty-${log.tupdate}-${index}`}
                          className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3 text-gray-500"
                        >
                          <p className={textStyles}>Warranty was edited</p>
                          <div className={sectionStyles}>
                            <p className={`${textStyles} font-bold`}>
                              {t('general.warrantyDetails')}
                            </p>
                            {renderDetails(log, warrantyKeys)}
                          </div>
                          <div className="text-right text-sm">
                            {log.tupdate
                              ? log.tupdate.length == 2
                                ? new Date(log.tupdate[1]).toLocaleTimeString()
                                : new Date(log.tupdate[0]).toLocaleTimeString()
                              : ''}
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              );
            })}
      </div>
    </Modal>
  );
}
